import { Button } from "@penmate/react-shared/ui/button";
import {
  DocumentDialog,
  DocumentDialogClose,
  DocumentDialogContent,
  DocumentDialogDescription,
  DocumentDialogTitle,
} from "@/components/ui/document-dialog";
import ISO_COUNTRY_CODES from "@/components/checkout/iso-phone-codes";
import { confirmSmsVerification, requestSmsVerification } from "@/api/smsVerification";
import { ChevronDownIcon } from "lucide-react";
import { useMemo, useState } from "react";
import { useAutoAnimate } from "@formkit/auto-animate/react";

type IsoCountryCode = {
  Iso2?: string;
  Name?: string;
  Dial?: string;
  Unicode?: string;
  [key: string]: any;
};

const DEFAULT_COUNTRY = "United States";

const normalizeDial = (dial: string): string => dial.replace(/^\+/, "").replace(/[^0-9]/g, "");
const normalizeDigits = (value: string): string => value.replace(/[^0-9]/g, "");

export function PhoneVerificationSheet(props: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onVerified: () => Promise<void> | void;
}) {
  const { open, onOpenChange, onVerified } = props;
  const [contentAutoAnimateRef] = useAutoAnimate<HTMLDivElement>();

  const countries = useMemo(() => ISO_COUNTRY_CODES as IsoCountryCode[], []);
  const [selectedCountry, setSelectedCountry] = useState(DEFAULT_COUNTRY);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState<"phone" | "code">("phone");
  const [numberWithCountryCode, setNumberWithCountryCode] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState(false);

  const countryInfo = useMemo(() => {
    const match = countries.find((c) => c && c.Name === selectedCountry);
    return match || null;
  }, [countries, selectedCountry]);

  const dialCode = useMemo(() => {
    const dialRaw = typeof countryInfo?.Dial === "string" ? countryInfo.Dial : "";
    return normalizeDial(dialRaw);
  }, [countryInfo]);

  const buildFullNumber = (): string => {
    const digits = normalizeDigits(phoneNumber);
    const dial = dialCode;
    if (!dial || digits.length < 5) return "";
    return `+${dial}${digits}`;
  };

  const reset = () => {
    setStep("phone");
    setSelectedCountry(DEFAULT_COUNTRY);
    setPhoneNumber("");
    setCode("");
    setNumberWithCountryCode("");
    setError(null);
    setIsBusy(false);
  };

  const sendCode = async () => {
    setError(null);
    const full = buildFullNumber();
    if (!full) {
      setError("Please enter a valid mobile / cell number.");
      return;
    }

    setIsBusy(true);
    try {
      const res = await requestSmsVerification({ phoneNumber: full });
      if (!res.ok) {
        const err = (res.data as any)?.error ?? (res.data as any) ?? null;
        const duplicate = !!err?.duplicate_number;
        const voip = !!err?.voip;
        if (duplicate) {
          setError("This number has already been registered. If this is an error, contact support.");
          return;
        }
        if (voip) {
          setError("Sorry, there is an issue verifying with your mobile carrier. Please contact support to finish activating your account.");
          return;
        }
        setError("Please enter a valid mobile / cell number.");
        return;
      }
      const success = !!(res.data as any)?.success;
      if (!success) {
        setError("Sorry, there is an issue verifying with your mobile carrier. Please try again.");
        return;
      }
      setNumberWithCountryCode(full);
      setStep("code");
    } finally {
      setIsBusy(false);
    }
  };

  const confirmCode = async () => {
    setError(null);
    const full = numberWithCountryCode || buildFullNumber();
    const vCode = normalizeDigits(code);
    if (!full || !vCode) {
      setError("The code you entered is invalid or has expired. Please request another code.");
      return;
    }

    setIsBusy(true);
    try {
      const res = await confirmSmsVerification({ phoneNumber: full, code: vCode });
      if (!res.ok || !(res.data as any)?.success) {
        const err = (res.data as any)?.error ?? (res.data as any) ?? null;
        if (err?.duplicate_number) {
          setError("Please enter a valid phone number.");
          return;
        }
        setError("The code you entered is invalid or has expired. Please make sure you entered the code correctly or request another code.");
        return;
      }
      await onVerified();
      onOpenChange(false);
      reset();
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <DocumentDialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (!next) reset();
      }}
    >
      <DocumentDialogContent className="w-[calc(100vw-1rem)] max-w-md !p-0 max-h-[calc(100vh-2rem)] supports-[height:100dvh]:max-h-[calc(100dvh-2rem)] overflow-hidden sm:h-auto sm:max-h-[calc(100vh-2rem)]">
        <DocumentDialogTitle className="sr-only">Verify phone</DocumentDialogTitle>
        <DocumentDialogDescription className="sr-only">Verify your phone number to send this message.</DocumentDialogDescription>

        <div className="flex min-h-0 flex-col">
          <div className="flex items-center justify-between border-b px-4 py-3">
            <div className="min-w-0">
              <div className="text-sm font-semibold">Verify your phone</div>
              <div className="mt-0.5 text-xs text-muted-foreground">Required to send free messages.</div>
            </div>
          </div>

          <div className="px-4 py-4" ref={contentAutoAnimateRef}>
            {step === "phone" ? (
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Country</label>
                  <div className="relative mt-1">
                    <select
                      className="w-full appearance-none rounded-xl border border-input bg-white/70 px-3 py-2 pr-9 text-sm shadow-sm"
                      value={selectedCountry}
                      onChange={(e) => setSelectedCountry(e.target.value)}
                      data-testid="phone-verify-country"
                    >
                      {countries.map((c, idx) => {
                        const name = typeof c?.Name === "string" ? c.Name : "";
                        if (!name) return null;
                        const dial = typeof c?.Dial === "string" ? c.Dial : "";
                        const flag = typeof c?.Unicode === "string" ? `${c.Unicode} ` : "";
                        return (
                          <option key={`${name}-${idx}`} value={name}>
                            {flag}
                            {name}
                            {dial ? ` (+${normalizeDial(dial) || dial})` : ""}
                          </option>
                        );
                      })}
                    </select>
                    <ChevronDownIcon className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
                  </div>
                </div>

                <div>
                  <label className="text-xs font-medium text-muted-foreground">Phone number</label>
                  <div className="mt-1 flex items-center gap-2">
                    <div className="rounded-xl border border-input bg-white/70 px-3 py-2 text-sm text-muted-foreground shadow-sm">
                      +{dialCode || "—"}
                    </div>
                    <input
                      className="w-full rounded-xl border border-input bg-white/70 px-3 py-2 text-sm shadow-sm outline-none focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/20"
                      placeholder="Phone number"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      inputMode="tel"
                      autoComplete="tel"
                      data-testid="phone-verify-number"
                    />
                  </div>
                </div>

                <Button type="button" className="w-full" onClick={() => void sendCode()} disabled={isBusy}>
                  {isBusy ? "Sending…" : "Send code"}
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="text-sm text-muted-foreground">
                  Enter the verification code sent to {numberWithCountryCode || buildFullNumber()}.
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Verification code</label>
                  <input
                    className="mt-1 w-full rounded-xl border border-input bg-white/70 px-3 py-2 text-base shadow-sm outline-none focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/20"
                    placeholder="Code"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    data-testid="phone-verify-code"
                  />
                </div>

                <div className="flex gap-2">
                  <Button type="button" variant="secondary" className="w-full" onClick={() => void sendCode()} disabled={isBusy}>
                    Resend code
                  </Button>
                  <Button type="button" className="w-full" onClick={() => void confirmCode()} disabled={isBusy}>
                    {isBusy ? "Confirming…" : "Confirm"}
                  </Button>
                </div>

                <div className="flex justify-center">
                  <Button type="button" variant="link" size="sm" className="h-auto p-0" onClick={() => setStep("phone")} disabled={isBusy}>
                    Change number
                  </Button>
                </div>
              </div>
            )}

            {error ? <div className="mt-3 text-xs text-destructive">{error}</div> : null}
          </div>

          <div className="flex items-center justify-end gap-2 border-t px-4 py-3">
            <DocumentDialogClose asChild>
              <Button type="button" variant="ghost" disabled={isBusy}>
                Cancel
              </Button>
            </DocumentDialogClose>
          </div>
        </div>
      </DocumentDialogContent>
    </DocumentDialog>
  );
}
